/** API client for Multi-Process OCR System */
import axios from 'axios';
import {
  DocumentUploadResponse,
  DocumentProcessResponse,
  DocumentReview,
  ExceptionCase,
  AuditLog,
  UnloadingReceipt,
  ReceiptValidation,
  ReceiptStatus,
  ReconciliationResult,
  VehicleCharacteristics,
  RegistrationDiscrepancy
} from '../types';

/**
 * Get API base URL based on environment
 * 
 * Priority:
 * 1. REACT_APP_API_URL environment variable (set during build for Cloud Run)
 * 2. If running in production and no env var, try to detect Cloud Run backend
 * 3. Local development fallback to localhost:8000
 */
const getApiBaseUrl = (): string => {
  // Explicitly set API URL (highest priority)
  if (process.env.REACT_APP_API_URL) {
    const url = process.env.REACT_APP_API_URL.trim();
    // Remove trailing slash if present
    return url.endsWith('/') ? url.slice(0, -1) : url;
  }

  // In production (Cloud Run), try to construct backend URL from current origin
  if (process.env.NODE_ENV === 'production') {
    // If frontend is on Cloud Run, backend is likely on same domain pattern
    // Try to construct backend URL: https://ocr-backend-xxx-uc.a.run.app
    const currentOrigin = window.location.origin;

    // Handle krelixir-ocr-frontend -> ocr-backend pattern
    if (currentOrigin.includes('krelixir-ocr-frontend')) {
      const backendUrl = currentOrigin.replace('krelixir-ocr-frontend', 'ocr-backend');
      console.log(`[API] Detected Cloud Run environment (krelixir), using backend: ${backendUrl}`);
      return backendUrl;
    }

    // If frontend URL contains 'ocr-frontend', replace with 'ocr-backend'
    if (currentOrigin.includes('ocr-frontend')) {
      const backendUrl = currentOrigin.replace('ocr-frontend', 'ocr-backend');
      console.log(`[API] Detected Cloud Run environment, using backend: ${backendUrl}`);
      return backendUrl;
    }

    // If frontend is on a different Cloud Run service, use relative URLs
    // This assumes frontend and backend are on same domain (not recommended but fallback)
    console.log('[API] Production mode, using relative URLs');
    return '';
  }

  // Local development - use localhost backend
  const localBackend = 'http://localhost:8000';
  console.log(`[API] Development mode, using local backend: ${localBackend}`);
  return localBackend;
};

const API_BASE_URL = getApiBaseUrl();

// Log the API base URL for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log(`[API] Base URL: ${API_BASE_URL}`);
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes timeout for long-running operations like OCR
});

// Dashboard Stats
export interface DashboardStats {
  total_documents: number;
  approved: number;
  rejected: number;
  processed: number;
  pending_review: number;
  exceptions: number;
  status_counts: Record<string, number>;
  process_counts: Record<string, number>;
  progress: {
    daily_fee: number;
    unloading: number;
    pallet: number;
    vehicle: number;
  };
  charts: {
    token_histogram: Array<{ filename: string; tokens: number; pages: number; process_type: string }>;
    upload_timeline: Array<{ month: string; count: number }>;
    doc_types: Array<{ type: string; count: number }>;
    pages_distribution: Array<{ pages: string; count: number }>;
    process_status: Array<Record<string, any>>;
  };
  token_usage: {
    total_tokens: number;
    total_cost_usd: number;
    records: Array<{
      document_id: string;
      filename: string;
      process_type: string;
      total_tokens: number;
      prompt_tokens: number;
      completion_tokens: number;
      cost_usd: number;
      model: string;
      num_calls: number;
      created_at: string;
    }>;
    by_process: Record<string, { tokens: number; cost: number; docs: number }>;
  };
}

export const fetchDashboardStats = async (): Promise<DashboardStats> => {
  const response = await api.get('/api/dashboard/stats');
  return response.data;
};

export const uploadDocument = async (
  formData: FormData
): Promise<DocumentUploadResponse> => {
  const response = await api.post('/api/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000, // 5 minutes for OCR processing
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        console.log(`Upload progress: ${percentCompleted}%`);
      }
    },
  });
  return response.data;
};

export const processDocument = async (
  documentId: string
): Promise<DocumentProcessResponse> => {
  const response = await api.post(`/api/documents/${documentId}/process`);
  return response.data;
};

export const getDocument = async (documentId: string): Promise<DocumentReview> => {
  const response = await api.get(`/api/documents/${documentId}`);
  return response.data;
};

export const listDocuments = async (
  status?: string,
  isException?: boolean
): Promise<DocumentReview[]> => {
  const params: any = {};
  if (status) params.status = status;
  if (isException !== undefined) params.is_exception = isException;
  const response = await api.get('/api/documents', { params });
  return response.data;
};

export const getExceptionQueue = async (): Promise<ExceptionCase[]> => {
  const response = await api.get('/api/exceptions');
  return response.data;
};

export const approveDailyFee = async (documentId: string): Promise<{ message: string; document_id: string }> => {
  const response = await api.post(`/api/documents/${documentId}/approve`);
  return response.data;
};

export const rejectDailyFee = async (
  documentId: string,
  reason: string
): Promise<{ message: string; document_id: string }> => {
  const response = await api.post(`/api/documents/${documentId}/reject`, null, {
    params: { reason },
  });
  return response.data;
};

export const getAuditTrail = async (documentId: string): Promise<AuditLog[]> => {
  const response = await api.get(`/api/documents/${documentId}/audit`);
  return response.data;
};

// ==================== Unloading Payment APIs ====================

export const uploadReceipt = async (
  formData: FormData
): Promise<{
  receipt_id?: string;
  validation?: ReceiptValidation;
  status_recommendation?: ReceiptStatus;
  extracted_data?: any;
  // Multiple receipts format
  document_filename?: string;
  total_receipts?: number;
  receipts?: Array<{
    receipt_id: string;
    receipt_index: number;
    fields: Record<string, any>;
    validation: ReceiptValidation;
    status_recommendation: ReceiptStatus;
    total_fields: number;
  }>;
}> => {
  const response = await api.post('/api/unloading-payment/receipts/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  });
  return response.data;
};

export const processReceipt = async (
  receiptId: string
): Promise<{ receipt_id: string; status_recommendation: ReceiptStatus }> => {
  const response = await api.post(`/api/unloading-payment/receipts/${receiptId}/process`);
  return response.data;
};

export const listReceipts = async (status?: string): Promise<{ receipts: UnloadingReceipt[] }> => {
  const params: any = {};
  if (status) params.status = status;
  const response = await api.get('/api/unloading-payment/receipts', { params });
  return response.data;
};

export const approveReceipt = async (receiptId: string): Promise<{ message: string; receipt_id: string }> => {
  const response = await api.post(`/api/unloading-payment/receipts/${receiptId}/approve`);
  return response.data;
};

export const rejectReceipt = async (
  receiptId: string,
  reason: string
): Promise<{ message: string; receipt_id: string }> => {
  const response = await api.post(`/api/unloading-payment/receipts/${receiptId}/reject`, null, {
    params: { reason },
  });
  return response.data;
};

// ==================== Pallet Reconciliation APIs ====================

export const uploadVoucher = async (
  formData: FormData
): Promise<{
  voucher_id: string;
  process_result: any;
  extracted_data?: any;
  pallet_summary?: any;
  total_fields_extracted?: number;
}> => {
  const response = await api.post('/api/pallet-reconciliation/vouchers/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  });
  return response.data;
};

export const simulateEmailReceive = async (
  sender: string,
  subject: string,
  body: string,
  attachments: File[]
): Promise<{ email_id: string; voucher_ids: string[] }> => {
  const formData = new FormData();
  formData.append('sender', sender);
  formData.append('subject', subject);
  formData.append('body', body);
  attachments.forEach((file) => formData.append('attachments', file));

  const response = await api.post('/api/pallet-reconciliation/vouchers/email', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  });
  return response.data;
};

export const processVoucher = async (
  voucherId: string
): Promise<{ voucher_id: string; process_result: any }> => {
  const response = await api.post(`/api/pallet-reconciliation/vouchers/${voucherId}/process`);
  return response.data;
};

export const runReconciliation = async (
  customerId: string,
  startDate: string,
  endDate: string
): Promise<ReconciliationResult> => {
  const response = await api.post('/api/pallet-reconciliation/reconciliation/run', null, {
    params: { customer_id: customerId, start_date: startDate, end_date: endDate },
  });
  return response.data;
};

export const getReconciliationReports = async (): Promise<{ reports: ReconciliationResult[] }> => {
  const response = await api.get('/api/pallet-reconciliation/reconciliation/reports');
  return response.data;
};

export const generateCustomerStatement = async (
  customerId: string,
  reconciliationId: string
): Promise<{ statement_id: string; customer_id: string }> => {
  const response = await api.post('/api/pallet-reconciliation/statements/generate', null, {
    params: { customer_id: customerId, reconciliation_id: reconciliationId },
  });
  return response.data;
};

export const sendReconciliationEmail = async (
  recipientEmail: string,
  customerId: string = 'CUST-001',
  customerName?: string
): Promise<{
  success: boolean;
  message: string;
  email_subject: string;
  email_preview: string;
  reconciliation_id: string;
  recipient: string;
}> => {
  const response = await api.post('/api/pallet-reconciliation/send-email', null, {
    params: { recipient_email: recipientEmail, customer_id: customerId, customer_name: customerName },
  });
  return response.data;
};

export const downloadReconciliationReport = async (
  reconciliationId: string,
  format: 'json' | 'html' | 'txt' = 'json'
): Promise<Blob> => {
  const response = await api.get(`/api/pallet-reconciliation/download-report/${reconciliationId}`, {
    params: { format },
    responseType: 'blob',
  });
  return response.data;
};

export const generateEmailReport = async (
  customerId: string = 'CUST-001',
  recipientEmail?: string
): Promise<{
  reconciliation_report: any;
  email_report: {
    subject: string;
    html_body: string;
    text_body: string;
    recipient_email: string;
    reconciliation_id: string;
  };
}> => {
  const response = await api.post('/api/pallet-reconciliation/reconciliation/generate-report', null, {
    params: { customer_id: customerId, recipient_email: recipientEmail },
  });
  return response.data;
};

// ==================== Vehicle Registration APIs ====================

export const uploadVehicle = async (
  formData: FormData,
  isImage: boolean = true
): Promise<{
  vehicle_id: string;
  characteristics: VehicleCharacteristics;
  discrepancies: RegistrationDiscrepancy[];
}> => {
  formData.append('is_image', isImage.toString());
  const response = await api.post('/api/vehicle-registration/vehicles/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  });
  return response.data;
};

export const analyzeVehicle = async (
  vehicleId: string
): Promise<{ vehicle_id: string; characteristics: VehicleCharacteristics }> => {
  const response = await api.post(`/api/vehicle-registration/vehicles/${vehicleId}/analyze`);
  return response.data;
};

export const getDiscrepancies = async (): Promise<{ discrepancies: RegistrationDiscrepancy[] }> => {
  const response = await api.get('/api/vehicle-registration/vehicles/discrepancies');
  return response.data;
};

export const approveVehicle = async (vehicleId: string): Promise<{ message: string; vehicle_id: string }> => {
  const response = await api.post(`/api/vehicle-registration/vehicles/${vehicleId}/approve`);
  return response.data;
};

export const getVehicleHistory = async (vehicleId: string): Promise<{ vehicle_id: string; history: any[] }> => {
  const response = await api.get(`/api/vehicle-registration/vehicles/${vehicleId}/history`);
  return response.data;
};

// ==================== Firebase Document Storage APIs ====================

export interface FirebaseDocument {
  document_id: string;
  filename: string;
  delivery_number: string | null;
  status: string;
  uploaded_at: string | null;
  firebase_storage_path: string | null;
  has_extracted_data: boolean;
  has_comparison_result: boolean;
}

export const listFirebaseDocuments = async (processType?: string): Promise<{
  documents: FirebaseDocument[];
  total: number;
}> => {
  const params = processType ? { process_type: processType } : {};
  const response = await api.get('/api/firebase/documents', { params });
  return response.data;
};

export interface DocumentPreviewResponse {
  signed_url: string | null;
  filename: string;
  content_type: string;
  document_id: string;
  file_unavailable?: boolean;
  fallback_data?: {
    extracted_fields: Record<string, { value: string; confidence?: number }>;
    ocr_text: string;
    status: string;
    uploaded_at: string;
    delivery_number?: string;
    has_comparison: boolean;
  };
}

export const getDocumentPreview = async (documentId: string): Promise<DocumentPreviewResponse> => {
  const response = await api.get(`/api/firebase/documents/${documentId}/preview`);
  return response.data;
};

export const fetchDocumentPreviewUrl = async (documentId: string): Promise<{
  document_id: string;
  filename: string;
  preview_url: string;
  content_type: string;
}> => {
  const response = await api.get(`/api/firebase/documents/${documentId}/preview-url`);
  return response.data;
};

export const uploadToFirebase = async (
  formData: FormData
): Promise<DocumentUploadResponse & {
  firebase_storage_path?: string;
  firebase_signed_url?: string;
}> => {
  const response = await api.post('/api/firebase/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000,
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        console.log(`Firebase upload progress: ${percentCompleted}%`);
      }
    },
  });
  return response.data;
};

export const reprocessDocument = async (
  documentId: string
): Promise<DocumentProcessResponse & {
  extracted_data?: any;
  message?: string;
}> => {
  const response = await api.post(`/api/documents/${documentId}/reprocess`);
  return response.data;
};

// ==================== ERP Data from GCS ====================

export interface ErpDataSection {
  section: string;
  headers: string[];
  data: Record<string, string>[];
}

export interface ErpDataResponse {
  bucket: string;
  filename: string;
  sections: ErpDataSection[];
  total_sections: number;
}

export const fetchErpData = async (processType?: string): Promise<ErpDataResponse> => {
  const params = processType ? { process_type: processType } : {};
  const response = await api.get('/api/erp-data', { params });
  return response.data;
};
